<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { Bell, Mail, Save } from '@lucide/vue'
import { useI18n } from 'vue-i18n'
import AppShell from '@/components/AppShell.vue'
import { api } from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import type { AlertSettings, AlertStrategy } from '@/types/monitoring'

interface Rule {
  id: 'cpu' | 'memory' | 'disk' | 'network' | 'offline'
  enabled: boolean
  threshold: number
  durationMinutes: number
  unit: 'percent' | 'network' | 'minutes'
  strategy: AlertStrategy
}

const { t } = useI18n()
const auth = useAuthStore()
const rules = ref<Rule[]>([])
const baseline = ref<AlertSettings | null>(null)
const recipients = ref('')
const cooldownMinutes = ref(15)
const smtpEnabled = ref(false)
const loading = ref(true)
const saving = ref(false)
const error = ref('')
const saved = ref(false)
const loaded = computed(() => baseline.value !== null)

function secondsToWholeMinutes(seconds: number): number {
  return Math.max(1, Math.round(seconds / 60))
}

const ruleNames = computed<Record<Rule['id'], string>>(() => ({
  cpu: t('alerts.cpu'),
  memory: t('alerts.memory'),
  disk: t('alerts.disk'),
  network: t('alerts.network'),
  offline: t('alerts.offline'),
}))

function buildRules(settings: AlertSettings): Rule[] {
  return [
    {
      id: 'cpu',
      enabled: settings.cpuEnabled,
      threshold: settings.cpuPercent,
      durationMinutes: settings.cpuDurationSeconds / 60,
      unit: 'percent',
      strategy: settings.cpuStrategy,
    },
    {
      id: 'memory',
      enabled: settings.memoryEnabled,
      threshold: settings.memoryPercent,
      durationMinutes: settings.memoryDurationSeconds / 60,
      unit: 'percent',
      strategy: settings.memoryStrategy,
    },
    {
      id: 'disk',
      enabled: settings.diskEnabled,
      threshold: settings.diskPercent,
      durationMinutes: settings.diskDurationSeconds / 60,
      unit: 'percent',
      strategy: settings.diskStrategy,
    },
    {
      id: 'network',
      enabled: settings.networkEnabled,
      threshold: settings.networkBytesPerSecond / 1_000_000,
      durationMinutes: settings.networkDurationSeconds / 60,
      unit: 'network',
      strategy: settings.networkStrategy,
    },
    {
      id: 'offline',
      enabled: settings.offlineEnabled,
      threshold: settings.offlineSeconds / 60,
      durationMinutes: settings.offlineDurationSeconds / 60,
      unit: 'minutes',
      strategy: settings.offlineStrategy,
    },
  ]
}

async function load(): Promise<void> {
  loading.value = true
  error.value = ''
  try {
    const settings = await api.alertSettings(auth.token)
    baseline.value = settings
    rules.value = buildRules(settings)
    recipients.value = settings.emailRecipients.join(', ')
    cooldownMinutes.value = secondsToWholeMinutes(settings.cooldownSeconds)
    smtpEnabled.value = await api
      .config(auth.token)
      .then((config) => config.smtpEnabled)
      .catch(() => false)
  } catch (reason) {
    baseline.value = null
    rules.value = []
    error.value = reason instanceof Error ? reason.message : t('errors.loadSettings')
  } finally {
    loading.value = false
  }
}

async function save(): Promise<void> {
  if (!baseline.value) return
  if (
    !Number.isInteger(cooldownMinutes.value) ||
    cooldownMinutes.value < 1 ||
    cooldownMinutes.value > 10_080
  ) {
    error.value = t('errors.invalidCooldown')
    return
  }
  const byId = Object.fromEntries(rules.value.map((rule) => [rule.id, rule])) as Record<
    Rule['id'],
    Rule
  >
  const settings: AlertSettings = {
    ...baseline.value,
    cpuEnabled: byId.cpu.enabled,
    memoryEnabled: byId.memory.enabled,
    diskEnabled: byId.disk.enabled,
    networkEnabled: byId.network.enabled,
    offlineEnabled: byId.offline.enabled,
    cpuPercent: byId.cpu.threshold,
    memoryPercent: byId.memory.threshold,
    diskPercent: byId.disk.threshold,
    networkBytesPerSecond: byId.network.threshold * 1_000_000,
    offlineSeconds: byId.offline.threshold * 60,
    cpuDurationSeconds: byId.cpu.durationMinutes * 60,
    memoryDurationSeconds: byId.memory.durationMinutes * 60,
    diskDurationSeconds: byId.disk.durationMinutes * 60,
    networkDurationSeconds: byId.network.durationMinutes * 60,
    offlineDurationSeconds: byId.offline.durationMinutes * 60,
    cpuStrategy: byId.cpu.strategy,
    memoryStrategy: byId.memory.strategy,
    diskStrategy: byId.disk.strategy,
    networkStrategy: byId.network.strategy,
    offlineStrategy: byId.offline.strategy,
    cooldownSeconds: cooldownMinutes.value * 60,
    emailRecipients: recipients.value
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean),
  }
  saving.value = true
  error.value = ''
  saved.value = false
  try {
    const updated = await api.updateAlertSettings(auth.token, settings)
    baseline.value = updated
    rules.value = buildRules(updated)
    cooldownMinutes.value = secondsToWholeMinutes(updated.cooldownSeconds)
    saved.value = true
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : t('errors.saveRules')
  } finally {
    saving.value = false
  }
}

onMounted(() => void load())
</script>

<template>
  <AppShell>
    <section class="max-w-5xl">
      <p class="eyebrow">{{ $t('alerts.eyebrow') }}</p>
      <h1 class="mt-2 font-display text-3xl font-bold text-ink">{{ $t('alerts.title') }}</h1>
      <p class="mt-3 text-sm text-slate-500">{{ $t('alerts.subtitle') }}</p>

      <div v-if="loading" class="mt-8 space-y-3">
        <div
          v-for="index in 5"
          :key="index"
          class="h-20 animate-pulse rounded-md bg-slate-100"
        ></div>
      </div>
      <div v-else-if="!loaded" class="panel mt-8 p-6">
        <p role="alert" class="text-sm text-coral-600">{{ error || $t('errors.loadSettings') }}</p>
        <button type="button" class="btn-quiet mt-4" @click="load">
          {{ $t('common.tryAgain') }}
        </button>
      </div>
      <form v-else class="mt-8" @submit.prevent="save">
        <div class="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
          <div
            class="grid min-w-[900px] grid-cols-[1fr_1.35fr_0.9fr_1.15fr] gap-4 border-b border-slate-200 bg-slate-50/80 px-4 py-3 text-xs font-medium text-slate-500 dark:border-slate-700"
          >
            <span>{{ $t('alerts.rule') }}</span
            ><span>{{ $t('alerts.condition') }}</span
            ><span>{{ $t('alerts.hold') }}</span
            ><span>{{ $t('alerts.strategy') }}</span>
          </div>
          <div
            v-for="rule in rules"
            :key="rule.id"
            class="grid min-w-[900px] grid-cols-[1fr_1.35fr_0.9fr_1.15fr] items-center gap-4 border-b border-slate-100 px-4 py-4 last:border-b-0 dark:border-slate-700"
          >
            <label class="flex items-center gap-3">
              <input
                v-model="rule.enabled"
                type="checkbox"
                class="size-4 accent-moss-600"
                :aria-label="$t('alerts.toggleRule', { rule: ruleNames[rule.id] })"
              />
              <span class="font-medium text-slate-700">{{ ruleNames[rule.id] }}</span>
            </label>
            <label class="flex items-center gap-2 text-sm text-slate-500">
              <span v-if="rule.id !== 'offline'">{{ $t('alerts.greaterThan') }}</span>
              <input
                v-model.number="rule.threshold"
                type="number"
                class="field !min-h-9 !w-28 !px-3"
                min="0"
                :max="rule.unit === 'percent' ? 100 : 1440"
                step="0.1"
                :disabled="!rule.enabled"
                :aria-label="$t('alerts.thresholdFor', { rule: ruleNames[rule.id] })"
              />
              <span class="font-mono text-xs">{{
                $t(
                  rule.unit === 'percent'
                    ? 'alerts.percentUnit'
                    : rule.unit === 'network'
                      ? 'alerts.megabytesPerSecond'
                      : 'alerts.minuteUnit',
                )
              }}</span>
            </label>
            <label v-if="rule.id !== 'offline'" class="flex items-center gap-2">
              <input
                v-model.number="rule.durationMinutes"
                type="number"
                class="field !min-h-9 !w-28 !px-3"
                min="0"
                max="1440"
                step="0.5"
                :disabled="!rule.enabled"
                :aria-label="$t('alerts.durationFor', { rule: ruleNames[rule.id] })"
              />
              <span class="text-sm text-slate-500">{{ $t('alerts.minutes') }}</span>
            </label>
            <span v-else class="text-sm text-slate-400">{{ $t('alerts.immediate') }}</span>
            <label>
              <span class="sr-only">{{
                $t('alerts.strategyFor', { rule: ruleNames[rule.id] })
              }}</span>
              <select
                v-model="rule.strategy"
                class="field !min-h-9 !py-0 text-sm"
                :disabled="!rule.enabled"
                :aria-label="$t('alerts.strategyFor', { rule: ruleNames[rule.id] })"
              >
                <option value="state_change">{{ $t('alerts.stateChange') }}</option>
                <option value="continuous">{{ $t('alerts.continuous') }}</option>
              </select>
            </label>
          </div>
        </div>

        <section
          class="mt-8 grid gap-6 border-b border-slate-200 pb-8 md:grid-cols-2 dark:border-slate-700"
        >
          <label class="block">
            <span class="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700"
              ><Mail class="size-4" aria-hidden="true" />{{ $t('alerts.recipients') }}</span
            >
            <input
              v-model="recipients"
              class="field"
              :placeholder="$t('alerts.recipientsPlaceholder')"
            />
            <span class="mt-2 block text-xs text-slate-400">{{ $t('alerts.recipientsHint') }}</span>
            <span
              class="mt-2 block text-xs"
              :class="smtpEnabled ? 'text-moss-700' : 'text-amber-500'"
              >{{ $t(smtpEnabled ? 'alerts.smtpReady' : 'alerts.smtpMissing') }}</span
            >
          </label>
          <label class="block">
            <span class="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700"
              ><Bell class="size-4" aria-hidden="true" />{{ $t('alerts.cooldown') }}</span
            >
            <div class="flex items-center gap-2">
              <input
                v-model.number="cooldownMinutes"
                type="number"
                class="field !w-32"
                min="1"
                max="10080"
                step="1"
              /><span class="text-sm text-slate-500">{{ $t('alerts.minutes') }}</span>
            </div>
            <span class="mt-2 block text-xs text-slate-400">{{ $t('alerts.cooldownHint') }}</span>
          </label>
        </section>

        <p v-if="error" role="alert" class="mt-5 text-sm text-coral-600">{{ error }}</p>
        <p v-if="saved" role="status" class="mt-5 text-sm text-moss-700">
          {{ $t('alerts.saved') }}
        </p>
        <div class="mt-6 flex justify-end">
          <button type="submit" class="btn-primary gap-2" :disabled="saving">
            <Save class="size-4" aria-hidden="true" />{{
              saving ? $t('common.saving') : $t('alerts.save')
            }}
          </button>
        </div>
      </form>
    </section>
  </AppShell>
</template>
